import { useCallback } from 'react';

import { useFormContext } from '@/components/react-hook-form';
import {
  CombinedFormShape,
  CombinedPaths,
} from '@/modules/dispositiveProvisions/dispositiveProvisions.types';

import { DispositiveProvisionsFormRecipient } from '../../DispositiveProvisionsForm.types';
import { RecipientsBasePath } from '../DispositiveProvisionsRecipient';

interface SetValueOptions {
  shouldDirty?: boolean;
  shouldTouch?: boolean;
  shouldValidate?: boolean;
}

/**
 * Type-safe setter for fields on a specific recipient in the DP form.
 *
 * react-hook-form's `setValue` collapses the value parameter to `never`
 * when the path argument is a union type (RecipientsBasePath has 3 members).
 * This hook centralises a single assertion so callers get full type checking
 * on field names and values without needing `as never` at every call site.
 */
export function useSetRecipientField(
  recipientsBasePath: RecipientsBasePath,
  idx: number
) {
  const { setValue } = useFormContext<CombinedFormShape>();

  return useCallback(
    <K extends keyof DispositiveProvisionsFormRecipient>(
      field: K,
      value: DispositiveProvisionsFormRecipient[K],
      options?: SetValueOptions
    ) => {
      // All three RecipientsBasePath members resolve to the same
      // DispositiveProvisionsFormRecipient shape, so this assertion is safe.
      setValue(
        `${recipientsBasePath}.${idx}.${String(field)}` as CombinedPaths,
        value as never,
        options
      );
    },
    [recipientsBasePath, idx, setValue]
  );
}
